import { Select, Tooltip } from "antd";
import { Field, Form, Formik } from "formik";
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ButtonLoader from "@/Components/ButtonLoader";
import { RefreshIcon } from "@/Components/Icons/RefreshIcon";
import { RejectIcon } from "@/Components/Icons/RejectIcon";
import { SelectDownIcon } from "@/Components/Icons/SelectDownIcon";
import {
  getSecurityCode,
  updateClientProfileData,
} from "@/reducers/clientprofile";
import { _fetchAddress } from "@/services/clientprofile";
import { TOOLTIPTEXT, toastMessageText } from "@/utilities/CommonText";
import { showErrorToast } from "@/utilities/utils";

import FieldErrorMessage from "../../FieldErrorMessage";
import { RequiredSymbol } from "../../RequiredSymbol";
import SelectNoContent from "../../SelectNoContent";

/**
 * MyProfile component responsible for rendering and managing the user's profile page.
 */
export const ClientProfile = () => {
  const dispatch = useDispatch();
  const { profileData, isLoading, contactProfileData } = useSelector(
    (state) => state.clientProfile,
  );

  const [profileObj, setProfileObj] = useState({});
  const [allRegions, setAllRegions] = useState([]);
  const [allCounties, setAllCounties] = useState([]);
  const [regionFromCounty, setRegionFromCounty] = useState([]);
  const [addList, setAddList] = useState([]);
  const [addLoading, setAddLoading] = useState(false);
  const { isPremiumAccount, clientUserAllPermissions } = useSelector(
    (state) => state.session,
  );
  /**
   * Updates the profile information using the profileData object and sets various states accordingly.
   */
  const updateProfileInfo = () => {
    const {
      profile,
      all_region = [],
      all_counties = [],
      region_from_county = [],
    } = profileData;
    setProfileObj(profile);
    setAllRegions(all_region);
    setAllCounties(all_counties);
    setRegionFromCounty(region_from_county);
  };
  useEffect(() => {
    if (_.isEmpty(profileData)) return;
    // setProfile_Data(profileData);
    updateProfileInfo();
  }, [profileData]);
  // if (_.isEmpty(leObj)) return null;

  /**
   * handleSecurityCode function handles the security code.
   */
  const handleSecurityCode = () => {
    dispatch(getSecurityCode());
  };

  /**
   * Fetches address data based on the client's postcode, processes the response, and updates the UI accordingly.
   */
  const findAddress = () => {
    let response = _fetchAddress(profileObj?.client_postcode);
    response
      .then((res) => {
        const { data } = res;
        let _data = {};
        Object.keys(data).map((item) => {
          _data[item.toLowerCase()] = data[item];
        });
        const { addresses, latitude, longitude } = _data;
        let _addresses = addresses.map((item) => {
          return item
            .split(", ")
            .filter((i) => i !== "")
            .join(", ");
        });
        if (addresses.length === 0) {
          showErrorToast(toastMessageText.noAddressFoundForPostCode);
        }
        setAddList(_addresses);
        setProfileObj({
          ...profileObj,
          client_lat: latitude,
          client_lon: longitude,
        });
      })
      .catch((error) => {
        const {
          response: {
            data: { Message },
          },
        } = error;
        setAddList([]);
        if (Message === "Bad Request: Invalid postcode.") {
          showErrorToast(toastMessageText.ukPostCodeOnly);
        } else {
          showErrorToast(toastMessageText.unableToConnect);
        }
      })
      .finally(() => {
        setAddLoading(false);
      });
  };

  /**
   * Updates the profile object with the new value for the specified name.
   */
  const handleChange = (name, value) => {
    setProfileObj({
      ...profileObj,
      [name]: value,
    });
  };

  return (
    <div className="ClientProfile_Wrapper tour-applocum-client-profile-update grid gap-6">
      <div className="shadow-[0px_1px_8px_0px_#E7EFFB rounded-[10px] bg-white px-5 py-6">
        <div className="flex justify-between">
          <h1 className="pb-6 text-[20px] font-semibold leading-[normal] text-black">
            Practice Profile
          </h1>
          {isLoading ? (
            ""
          ) : (
            <p className="text-[14px] font-medium capitalize text-black">
              Actioned By :{"   "}
              <b>
                {profileObj?.actioned_by || "-"}|
                {moment(
                  profileObj?.actioned_at,
                  "DD/MM/YYYY HH:mm",
                  true,
                ).isValid()
                  ? moment(profileObj?.actioned_at, "DD/MM/YYYY HH:mm").format(
                      "DD/MM/YYYY",
                    )
                  : "-"}
                |
                {moment(
                  profileObj?.actioned_at,
                  "DD/MM/YYYY HH:mm",
                  true,
                ).isValid()
                  ? moment(profileObj?.actioned_at, "DD/MM/YYYY HH:mm").format(
                      "hh:mm A",
                    )
                  : "-"}
              </b>
            </p>
          )}
        </div>

        <Formik
          initialValues={{
            clientName: profileObj?.client_name || "",
            practiceCode: profileObj?.practice_code || "",
            contactNumber: profileObj?.client_telno || "",
            referenceNumber: profileObj?.client_refno || "",
            securityCode: profileObj?.security_code || "",
            vatNumber: profileObj?.client_vat_number || "",
            website: profileObj?.client_website || "",
            parking: profileObj?.parking || "",
            address: profileObj?.client_A1 || "",
            postCode: profileObj?.client_postcode || "",
            county: profileObj?.county_id || [],
            foodDrink: profileObj?.food_drink || "",
            clientNotes: profileObj?.client_notes || "",
            region: profileObj?.region || [],
            isIr35: profileObj?.is_ir35 || "",
            clientLat: profileObj?.client_lat || "",
            clientLon: profileObj?.client_lon || "",
            itSystems: profileObj?.IT_system_name,
            city: profileObj?.client_city || "",
            email: profileObj?.email || "",
          }}
          validate={(values) => {
            const errors = {};
            if (!values.county) {
              errors.county = "County is required";
            }
            if (!values.region) {
              errors.region = "Region is required";
            }
            return errors;
          }}
          onSubmit={(values, { setSubmitting }) => {
            dispatch(
              updateClientProfileData({
                values,
                setSubmitting,
                addList,
                setAddList,
              }),
            );
          }}
          enableReinitialize={true}
        >
          {({
            submitForm,
            isSubmitting,
            errors,
            touched,
            isValid,
            values,
            setFieldValue,
          }) => (
            <Form>
              <div className="grid gap-6">
                <div className="flex flex-wrap gap-[17px]">
                  {/* <ProfileAvatar /> */}
                  <div className="grid grow grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
                    <div className="NAME grid gap-3">
                      <label
                        htmlFor="clientName"
                        className="text-[14px] font-medium leading-[normal] text-black"
                      >
                        Name
                      </label>
                      <Field
                        type="text"
                        className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black !outline-none !ring-0 focus:border-primary ${
                          errors.clientName ? "!border-red-500" : ""
                        }`}
                        autoComplete="off"
                        name="clientName"
                        value={values.clientName}
                        placeholder="Enter Name"
                        disabled
                      />
                    </div>
                    <div className="PRACTICECODE grid gap-3">
                      <label
                        htmlFor="practiceCode"
                        className="text-[14px] font-medium leading-[normal] text-black"
                      >
                        Practice Code
                      </label>
                      <Field
                        type="text"
                        className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black !outline-none !ring-0 focus:border-primary ${
                          errors.practiceCode ? "!border-red-500" : ""
                        }`}
                        autoComplete="off"
                        name="practiceCode"
                        value={values.practiceCode}
                        placeholder="Enter Practice Code"
                        disabled
                      />
                    </div>
                    <div className="EMAIL grid gap-3">
                      <label
                        htmlFor="email"
                        className="text-[14px] font-medium leading-[normal] text-black"
                      >
                        Email
                      </label>
                      <Field
                        type="text"
                        className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black !outline-none !ring-0 focus:border-primary ${
                          errors.email ? "!border-red-500" : ""
                        }`}
                        autoComplete="off"
                        name="email"
                        value={values.email}
                        placeholder="Enter Practice Code"
                        disabled
                      />
                    </div>
                    <div className="MOBILENUMBER grid gap-3">
                      <label
                        htmlFor="contactNumber"
                        className="text-[14px] font-medium leading-[normal] text-black"
                      >
                        Phone Number
                      </label>
                      <Field
                        type="text"
                        className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black !outline-none !ring-0 focus:border-primary ${
                          errors.contactNumber ? "!border-red-500" : ""
                        }`}
                        autoComplete="off"
                        name="contactNumber"
                        value={values.contactNumber}
                        placeholder="Enter Contact Number"
                        disabled
                      />
                    </div>
                    <div className="REFERENCENUMBER grid gap-3">
                      <label
                        htmlFor="referenceNumber"
                        className="text-[14px] font-medium leading-[normal] text-black"
                      >
                        Reference Number
                      </label>
                      <Field
                        type="text"
                        className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black !outline-none !ring-0 focus:border-primary ${
                          errors.referenceNumber ? "!border-red-500" : ""
                        }`}
                        autoComplete="off"
                        name="referenceNumber"
                        value={values.referenceNumber}
                        placeholder="Enter Reference Number"
                        disabled
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 items-start gap-6 sm:grid-cols-2 md:grid-cols-3">
                  <div className="SECURITYCODE grid gap-3">
                    <label
                      htmlFor="securityCode"
                      className="text-[14px] font-medium leading-[normal] text-black"
                    >
                      Security Code <RequiredSymbol />
                    </label>
                    <div className="relative">
                      <Field
                        type="text"
                        className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black !outline-none !ring-0 focus:border-primary ${
                          errors.securityCode ? "!border-red-500" : ""
                        }`}
                        autoComplete="off"
                        name="securityCode"
                        value={values.securityCode}
                        placeholder="Enter Security Code"
                        disabled
                      />
                      <Tooltip title={TOOLTIPTEXT.securityCode}>
                        <button
                          type="button"
                          className={`absolute right-4 top-2 h-[24px] w-[24px] !border-0 !bg-none p-0 !ring-0`}
                          onClick={() => {
                            handleSecurityCode();
                          }}
                        >
                          <RefreshIcon />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="POSTCODE grid gap-3">
                    <label
                      htmlFor="postCode"
                      className="text-[14px] font-medium leading-[normal] text-black"
                    >
                      Postcode <RequiredSymbol />
                    </label>
                    <div>
                      <div className="relative">
                        <Field
                          type="text"
                          className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black !outline-none !ring-0 focus:border-primary ${
                            errors.postCode ? "!border-red-500" : ""
                          }`}
                          autoComplete="off"
                          name="postCode"
                          value={values.postCode}
                          placeholder="Enter Post Code"
                          validate={(value) => {
                            if (!value || value.trim() === "") {
                              return "Postcode is required";
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 font-bold text-primary outline-none !ring-0"
                          onClick={() => {
                            if (
                              profileObj?.client_postcode === "" ||
                              profileObj?.client_postcode === null ||
                              profileObj?.client_postcode === undefined
                            ) {
                              showErrorToast(toastMessageText.postCodeRequired);
                              return;
                            }
                            if (addLoading) return;
                            setAddLoading(true);
                            findAddress();
                          }}
                        >
                          {addLoading ? "Loading..." : "Find Address"}
                        </button>
                      </div>
                      <FieldErrorMessage name="postCode" />
                    </div>
                  </div>
                  {addList.length > 0 ? (
                    <div className="SELECTADDRESS grid gap-3">
                      <label
                        htmlFor="address"
                        className="text-[14px] font-medium leading-[normal] text-black"
                      >
                        Select Address
                      </label>
                      <Select
                        showSearch
                        style={{ width: "100%" }}
                        placeholder={"Select Address"}
                        optionFilterProp="children"
                        className="ease min-h-[40px] w-full overflow-hidden rounded-lg border border-lightGrey text-[14px] font-medium leading-[normal] text-black transition-all duration-200 focus:border-primary"
                        suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
                        onChange={(value) => {
                          let county = _.last(value.split(",")).trim();
                          let selectedAddressCounty = allCounties.find(
                            (item) =>
                              item.name.toLowerCase() === county.toLowerCase(),
                          );
                          let regionFromCountyId =
                            regionFromCounty[selectedAddressCounty.id];

                          setProfileObj({
                            ...profileObj,
                            client_A1: value,
                            county_id: selectedAddressCounty.id,
                            region: regionFromCountyId,
                          });
                        }}
                        allowClear={{
                          clearIcon: (
                            <RejectIcon cls="w-[20px] h-[20px] text-red" />
                          ),
                        }}
                        notFoundContent={<SelectNoContent />}
                      >
                        {addList.map((item, index) => {
                          return (
                            <Select.Option value={item} key={index}>
                              {item}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </div>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 items-start gap-6 sm:grid-cols-2 md:grid-cols-3">
                  <div className="ADDRESS grid gap-3">
                    <label
                      htmlFor="address"
                      className="text-[14px] font-medium leading-[normal] text-black"
                    >
                      Address <RequiredSymbol />
                    </label>
                    <div>
                      <Field
                        type="text"
                        className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black !outline-none !ring-0 focus:border-primary ${
                          errors.address ? "!border-red-500" : ""
                        }`}
                        autoComplete="off"
                        name="address"
                        value={values.address}
                        placeholder="Enter Address"
                        onChange={(e) => {
                          handleChange("client_A1", e.target.value);
                        }}
                        validate={(value) => {
                          if (!value || value.trim() === "") {
                            return "Address is required";
                          }
                        }}
                      />
                      <FieldErrorMessage name="address" />
                    </div>
                  </div>
                  <div className="COUNTY grid gap-3">
                    <label
                      htmlFor="county"
                      className="text-[14px] font-medium leading-[normal] text-black"
                    >
                      County <RequiredSymbol />
                    </label>
                    <div className="flex flex-col">
                      <Select
                        showSearch
                        style={{ width: "100%" }}
                        placeholder={"Select"}
                        optionFilterProp="children"
                        className="ease min-h-[40px] w-full overflow-hidden rounded-lg border border-lightGrey text-[14px] font-medium leading-[normal] text-black transition-all duration-200 focus:border-primary"
                        suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
                        // allowClear={{
                        //   clearIcon: (
                        //     <RejectIcon cls="w-[20px] h-[20px] text-red" />
                        //   ),
                        // }}
                        value={values.county == "" ? null : values.county}
                        notFoundContent={<SelectNoContent />}
                        disabled
                      >
                        {allCounties?.map((item, index) => {
                          const { id, name } = item;
                          return (
                            <Select.Option value={id} key={index}>
                              {name}
                            </Select.Option>
                          );
                        })}
                      </Select>
                      <FieldErrorMessage name="county" />
                    </div>
                  </div>
                  <div className="REGION grid gap-3">
                    <label
                      htmlFor="region"
                      className="text-[14px] font-medium leading-[normal] text-black"
                    >
                      Region <RequiredSymbol />
                    </label>
                    <div className="flex flex-col">
                      <Select
                        showSearch
                        style={{ width: "100%" }}
                        placeholder={"Select"}
                        optionFilterProp="children"
                        className="ease min-h-[40px] w-full overflow-hidden rounded-lg border border-lightGrey text-[14px] font-medium leading-[normal] text-black transition-all duration-200 focus:border-primary"
                        suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
                        // allowClear={{
                        //   clearIcon: (
                        //     <RejectIcon cls="w-[20px] h-[20px] text-red" />
                        //   ),
                        // }}
                        value={values.region == "" ? null : values.region}
                        notFoundContent={<SelectNoContent />}
                        disabled
                      >
                        {allRegions.map((item, index) => {
                          const { id, name } = item;
                          return (
                            <Select.Option value={id} key={index}>
                              {name}
                            </Select.Option>
                          );
                        })}
                      </Select>
                      <FieldErrorMessage name="region" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
                  <div className="VATNUMBER grid gap-3">
                    <label
                      htmlFor="vatNumber"
                      className="text-[14px] font-medium leading-[normal] text-black"
                    >
                      VAT Number (If Applicable)
                    </label>
                    <Field
                      type="text"
                      className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black !outline-none !ring-0 focus:border-primary ${
                        errors.vatNumber ? "!border-red-500" : ""
                      }`}
                      autoComplete="off"
                      name="vatNumber"
                      value={values.vatNumber}
                      placeholder="Enter VAT Number"
                      onChange={(e) => {
                        handleChange("client_vat_number", e.target.value);
                      }}
                    />
                  </div>
                  <div className="WEBSITE grid gap-3">
                    <label
                      htmlFor="website"
                      className="text-[14px] font-medium leading-[normal] text-black"
                    >
                      Website
                    </label>
                    <Field
                      type="text"
                      className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black !outline-none !ring-0 focus:border-primary ${
                        errors.website ? "!border-red-500" : ""
                      }`}
                      autoComplete="off"
                      name="website"
                      value={values.website}
                      placeholder="Enter Website Link"
                      onChange={(e) => {
                        handleChange("client_website", e.target.value);
                      }}
                    />{" "}
                  </div>
                  <div className="PARKING grid gap-3">
                    <label
                      htmlFor="parking"
                      className="text-[14px] font-medium leading-[normal] text-black"
                    >
                      Parking
                    </label>
                    <Field
                      type="text"
                      className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black !outline-none !ring-0 focus:border-primary ${
                        errors.practiceCode ? "!border-red-500" : ""
                      }`}
                      autoComplete="off"
                      name="parking"
                      value={values.parking}
                      placeholder="Enter Parking"
                      onChange={(e) => {
                        handleChange("parking", e.target.value);
                      }}
                    />{" "}
                  </div>
                </div>

                <div className="flex justify-end gap-5">
                  {!clientUserAllPermissions ||
                  clientUserAllPermissions.is_allow_to_edit_profile === 1 ? (
                    <>
                      <button
                        type="button"
                        className={`transition-linear relative w-[120px] rounded-lg border border-primary bg-primary text-[14px] font-medium text-white hover:bg-none hover:text-primary ${isLoading ? "cursor-wait" : "cursor-pointer"}`}
                        onClick={submitForm}
                        disabled={isSubmitting || isLoading}
                      >
                        {isSubmitting ? <ButtonLoader /> : <span>Update</span>}
                      </button>
                      <button
                        className={`transition-linear h-[40px] w-[120px] rounded-lg border border-primary bg-white text-[14px] font-medium leading-[normal] text-primary hover:bg-primary hover:text-white ${isLoading ? "cursor-wait" : "cursor-pointer"}`}
                        onClick={updateProfileInfo}
                        type="button"
                        disabled={isLoading}
                      >
                        Reset
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};
